/**
 * Lotus tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const lotusTattoosPack: ToilePack = {
    slug: 'lotus-tattoos',
    category: 'tattoo',
    name: 'Lotus tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'water', label: 'Lotus on water', subject: 'an open lotus flower floating on a few lines of water, two round leaves beside it and a closed bud'},
        {key: 'unalome', label: 'Lotus and unalome', subject: 'a small lotus at the base of an unalome, a spiral that straightens into a line with three dots at the top'},
        {key: 'mandala', label: 'Lotus mandala', subject: 'a lotus seen from above with its petals in several rings, opening out into a round mandala'},
    ],
    styles: [
        {key: 'traditional', label: 'Traditional', style: 'American traditional tattoo flash, bold black outlines, heavy black shading and a limited flat palette', palette: 'red, yellow and green accents with black', framing: 'centered with a wide white margin'},
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin, the dots reading cleanly'},
    ],
    directions: ['a peony', 'pink ink', 'wrist'],
    directionHint: 'try: a peony',
    seo: {
        title: 'Lotus tattoo designs: lotus on water, unalome, mandala',
        metaDescription: 'Nine lotus tattoo designs: a lotus on water, a lotus beneath an unalome and a lotus mandala, in three styles.',
        h1: 'Lotus tattoo designs: lotus on water, lotus and unalome, lotus mandala',
        tagline: 'Three lotuses, each drawn in traditional flash, fine line and dotwork on white paper.',
        intro: 'The lotus rises clean from muddy water and is worn for purity and new beginnings in Hindu and Buddhist traditions. The nine sheets show an open lotus on water, a lotus at the base of an unalome, the Buddhist line for the path, and a lotus opening into a mandala. Each is drawn as traditional flash, fine line and dotwork. Type a different flower or twist, such as a peony, and the same styles can be asked for.',
        useCases: [
            'a wrist or ankle lotus',
            'a forearm piece with an unalome',
            'a round mandala lotus for the back or chest',
            'a printed sheet to compare styles',
        ],
        faq: [
            {q: 'What is on the page?', a: 'Nine lotus designs: a lotus on water, a lotus with an unalome and a lotus mandala, each in three styles.'},
            {q: 'What is an unalome?', a: 'It is the Buddhist spiral line that straightens into a single line, with three dots at the top, drawn here beneath the lotus.'},
            {q: 'Can I ask for a peony or a different colour?', a: 'Yes. Type a different flower or twist, such as a peony or pink ink; the result is a new drawing, not a guaranteed match.'},
            {q: 'What is the difference between the styles?', a: 'Traditional flash has bold outlines and a few accent colours. Fine line is one uniform hairline with little shading. Dotwork builds the shadows from dots.'},
        ],
    },
}
